<template>
  <!-- Incidencias del proyecto (módulo Issues): SOLO LECTURA. Los KPIs se derivan de las filas. -->
  <summary-card :title="$t('prm_dash_incidents_title')" :subtitle="$t('prm_dash_incidents_subtitle')" :mode="mode" chip="incid">
    <template #icon><b-icon pack="fas" icon="triangle-exclamation" size="is-small"></b-icon></template>
    <template #extra><span class="count">{{ incidents.length }} {{ $t('prm_summary_records') }}</span></template>

    <!-- KPIs (calculados de las incidencias) -->
    <div class="tiles">
      <div v-for="t in tiles" :key="t.key" class="tile">
        <span class="tile-label">{{ $t(t.label) }}</span>
        <span class="tile-value" :style="{ color: t.accent || '#1e3046' }">{{ t.value }}</span>
      </div>
    </div>

    <!-- Registro de incidencias (solo lectura, con badges de gravedad/estado y semáforo). -->
    <div class="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th class="idc">{{ $t('prm_summary_col_id') }}</th>
            <th>{{ $t('prm_summary_s04_col_date') }}</th>
            <th>{{ $t('prm_dash_incidents_col_class') }}</th>
            <th>{{ $t('prm_dash_incidents_col_type') }}</th>
            <th>{{ $t('prm_dash_incidents_col_discipline') }}</th>
            <th>{{ $t('prm_dash_incidents_col_origin') }}</th>
            <th>{{ $t('prm_summary_col_description') }}</th>
            <th>{{ $t('prm_summary_col_owner') }}</th>
            <th>{{ $t('prm_summary_col_status') }}</th>
            <th>{{ $t('prm_summary_col_target_date') }}</th>
            <th>{{ $t('prm_dash_incidents_col_days') }}</th>
            <!-- Bloqueante: oculto hasta confirmar con el cliente (Issue no tiene ese dato de forma fiable). -->
            <!-- <th>{{ $t('prm_dash_incidents_col_blocking') }}</th> -->
            <th class="c"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in incidents" :key="row.id">
            <td class="idc">{{ row.code || (i + 1) }}</td>
            <td>{{ fmtDate(row.date) }}</td>
            <td>
              <span v-if="row.classification" class="st-badge" :class="'sev-' + severity(row).level">{{ row.classification }}</span>
              <span v-else>-</span>
            </td>
            <td>{{ row.type || '-' }}</td>
            <td>{{ row.discipline || '-' }}</td>
            <td>{{ row.origin || '-' }}</td>
            <td class="desc">{{ row.description || '-' }}</td>
            <td>{{ row.owner || '-' }}</td>
            <td><span class="st-badge" :class="'st-' + row.status">{{ $t('prm_summary_risk_status_' + row.status) }}</span></td>
            <td>{{ fmtDate(row.targetDate) }}</td>
            <td>{{ row.daysOpen }}</td>
            <!-- <td>bloqueante (oculto)</td> -->
            <td class="c"><sem-dot :state="incidentColor(row)"></sem-dot></td>
          </tr>
          <tr v-if="!incidents.length"><td class="empty" colspan="12">{{ $t('prm_summary_empty') }}</td></tr>
        </tbody>
      </table>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SemDot from './sem-dot.vue'
  import { incidentColor, incidentSeverity, ACCENT } from './dashboard-model'

  export default {
    name: 'PrmDashIncidents',

    components: { SummaryCard, SemDot },

    props: {
      incidents: { type: Array, default: () => [] }, // incidencias reales del proyecto (fuente Issues)
      mode: { type: Boolean, default: false },
    },

    data () {
      return { today: new Date().toISOString().slice(0, 10) }
    },

    computed: {
      // KPIs derivados de las incidencias (los recuentos por gravedad y vencidas solo cuentan abiertas).
      stats () {
        const rows = this.incidents
        const open = rows.filter((r) => r.status !== 'closed')
        const closed = rows.filter((r) => r.status === 'closed')
        const byLevel = (level) => open.filter((r) => incidentSeverity(r.classCode).level === level).length
        const avgClose = closed.length
          ? Math.round(closed.reduce((s, r) => s + (Number(r.daysOpen) || 0), 0) / closed.length)
          : 0
        return {
          open: open.length,
          weStop: byLevel('we_stop'),
          verySerious: byLevel('very_serious'),
          serious: byLevel('serious'),
          minor: byLevel('minor'),
          overdue: open.filter((r) => r.targetDate && r.targetDate < this.today).length,
          avgClose,
          mainOrigin: this.topOrigin(),
        }
      },
      tiles () {
        const s = this.stats
        return [
          { key: 'open', label: 'prm_dash_incidents_open', value: String(s.open), accent: s.open > 0 ? ACCENT.bad : ACCENT.good },
          { key: 'weStop', label: 'prm_dash_incidents_we_stop', value: String(s.weStop), accent: s.weStop > 0 ? ACCENT.bad : '' },
          { key: 'verySerious', label: 'prm_dash_incidents_very_serious', value: String(s.verySerious), accent: s.verySerious > 0 ? ACCENT.bad : '' },
          { key: 'serious', label: 'prm_dash_incidents_serious', value: String(s.serious), accent: s.serious > 0 ? ACCENT.warn : '' },
          { key: 'minor', label: 'prm_dash_incidents_minor', value: String(s.minor) },
          { key: 'overdue', label: 'prm_dash_incidents_overdue', value: String(s.overdue), accent: s.overdue > 0 ? ACCENT.bad : '' },
          { key: 'avgClose', label: 'prm_dash_incidents_avg_close', value: `${s.avgClose} ${this.$t('prm_dash_days')}` },
          { key: 'mainOrigin', label: 'prm_dash_incidents_main_origin', value: s.mainOrigin || '-' },
          // Bloqueantes: KPI oculto hasta confirmar con el cliente (Issue no tiene el dato).
        ]
      },
    },

    methods: {
      incidentColor (r) { return incidentColor(r) },
      severity (r) { return incidentSeverity(r.classCode) },
      // Fecha ISO (Y-m-d) del backend a DD/MM/YYYY; rayita si viene vacía.
      fmtDate (v) {
        if (!v) return '-'
        const [y, m, d] = v.split('-')
        return (d && m && y) ? `${d}/${m}/${y}` : v
      },
      // Origen más frecuente entre las incidencias registradas.
      topOrigin () {
        const count = {}
        let top = ''
        let max = 0
        for (const r of this.incidents) {
          const o = (r.origin || '').trim()
          if (!o) continue
          count[o] = (count[o] || 0) + 1
          if (count[o] > max) { max = count[o]; top = o }
        }
        return top
      },
    },
  }
</script>

<style lang="scss" scoped>
  .count { margin-left: auto; font-size: 9px; font-weight: 400; text-transform: none; color: #9aa4b2; }

  .tiles {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 8px;
    padding: 10px; border-bottom: 1px solid #eef0f3;
  }
  .tile { display: flex; flex-direction: column; gap: 3px; border: 1px solid #eef0f3; background: #fafbfc; border-radius: 6px; padding: 7px 9px; }
  .tile-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #6b7787; line-height: 1.25; min-height: 22px; }
  .tile-value { font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .tbl-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .03em; font-weight: 700; padding: 5px 8px; text-align: left; white-space: nowrap; }
  th.c { text-align: center; }
  th.idc { width: 42px; }
  td { border-bottom: 1px solid #eef0f3; padding: 3px 8px; font-size: 11px; color: #2c3a4d; vertical-align: middle; white-space: nowrap; text-align: left; }
  td.idc { text-align: left; color: #3a6bc8; font-weight: 600; }
  td.c { text-align: center; }
  td.desc { min-width: 200px; white-space: normal; }
  td.empty { text-align: center; color: #9aa4b2; padding: 12px; }

  // Cebra + hover como las demás tablas.
  tbody tr:nth-child(even) td { background: #f9fafb; }
  tbody tr:hover td { background: #eef2f7; }

  // Badges de gravedad (clases de tkelevator) y estado.
  .st-badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; white-space: nowrap; }
  .sev-we_stop, .sev-very_serious { background: #fdeaea; color: #c0392b; border: 1px solid #f0b6b0; }
  .sev-serious { background: #fdf3e2; color: #b9770e; border: 1px solid #f0d6a8; }
  .sev-minor { background: #eaf6ee; color: #1e8449; border: 1px solid #b7dfc4; }
  .sev-other { background: #eef0f3; color: #66707d; border: 1px solid #d6dbe2; }
  .st-open { background: #eaf1fb; color: #2b5cad; border: 1px solid #c3d5f2; }
  .st-closed { background: #eef0f3; color: #6b7787; border: 1px solid #d6dbe2; }
</style>
